/**
 * URL Utilities
 * Low-level primitives for URL parsing, CDN resolution, bucket extraction,
 * and GCS-to-CDN URL transformation. Used as building blocks by image.utils.ts
 * and video.utils.ts for DRM-protected media URL generation.
 */

import { coursesFirebaseProductionConfig, coursesFirebaseStagingConfig, getConfig } from '../../core-layer/config'
import {
  AU_MEMBERSHIP_CDN_MAPPINGS,
  isAssetsDrmImageUrl,
  membershipCdnIndex,
  membershipGcsObjectPath,
  resolveMembershipCdnUrl,
} from '../../http-layer/utils/regional-host'
import { FIREBASE_STORAGE_BASE_URL } from './constants'
import type { CDNMapping } from '../types'

export type { CDNMapping } from '../types'

const config = getConfig()

/**
 * Generates CDN hostname to bucket mappings from config Firebase values
 *
 * Returns mappings for both staging and production environments to support
 * cross-environment URLs (e.g., production users viewing staging content)
 *
 * Production mappings:
 *   - cdn.courses.apisystem.tech → gcs_revex-membership-production
 *   - cdn.apisystem.tech → gcs_highlevel-backend.appspot.com
 *
 * Staging mappings:
 *   - staging.cdnmembership.apisystem.tech → gcs_revex-membership-staging
 *   - staging.cdn.msgsndr.com → gcs_highlevel-staging.appspot.com
 *
 * Example transformation:
 *   Input:  https://cdn.courses.apisystem.tech/memberships/abc/product/xyz
 *   Output: https://assetsdrm.clientclub.net/images/courses/gcs_revex-membership-production/memberships/abc/product/xyz?fmt=webp&wdt=500
 */
export function getCDNMappings(): CDNMapping[] {
  // Use exported Firebase configs from core-layer/config
  // This ensures we support all CDN URLs from both environments
  const stagingFirebase = coursesFirebaseStagingConfig
  const productionFirebase = coursesFirebaseProductionConfig

  return [
    // Production mappings
    {
      hostname: new URL(productionFirebase.membershipCdnUrl).hostname,
      bucket: productionFirebase.membershipStorageBucket,
      environment: 'production',
    },
    {
      hostname: new URL(productionFirebase.cdnUrl).hostname,
      bucket: productionFirebase.storageBucket,
      environment: 'production',
    },

    // Staging mappings
    {
      hostname: new URL(stagingFirebase.membershipCdnUrl).hostname,
      bucket: stagingFirebase.membershipStorageBucket,
      environment: 'staging',
    },
    ...AU_MEMBERSHIP_CDN_MAPPINGS,
    {
      hostname: new URL(stagingFirebase.oldCdnUrl).hostname,
      bucket: stagingFirebase.storageBucket,
      environment: 'staging',
    },
  ]
}

/**
 * Checks if a URL is from a known CDN and returns mapping info
 *
 * @param url - Full URL to check against CDN hostname mappings
 * @returns CDNMapping object if URL matches a known CDN, null otherwise
 *
 * @example
 * getCDNMapping('https://cdn.courses.apisystem.tech/memberships/path/file.jpg')
 * // Returns: { hostname: 'cdn.courses.apisystem.tech', bucket: 'revex-membership-production', environment: 'production' }
 *
 * @example
 * getCDNMapping('https://example.com/image.jpg')
 * // Returns: null
 */
export function getCDNMapping(url: string): CDNMapping | null {
  try {
    const urlObj = new URL(url)
    const mappings = getCDNMappings()
    return mappings.find(mapping => mapping.hostname === urlObj.hostname) || null
  } catch {
    return null
  }
}

/**
 * Extracts file path from a CDN URL (pathname without leading slash)
 *
 * @param url - CDN URL to extract file path from
 * @returns File path portion of the URL
 *
 * @example
 * extractCDNFilePath('https://cdn.courses.apisystem.tech/memberships/path/file.jpg')
 * // Returns: 'memberships/path/file.jpg'
 */
export function extractCDNFilePath(url: string): string {
  try {
    const urlObj = new URL(url)
    // Legacy central CDN URLs encode slashes as %2F in the pathname (Firebase-style).
    // Decode so membership path detection and DRM lookups use real path segments.
    const rawPath = urlObj.pathname.substring(1)
    return decodeURIComponent(rawPath)
  } catch {
    // If it's not a valid URL, assume it's already a file path
    return url
  }
}

/**
 * Extracts file path from a GCS or Firebase Storage URL.
 * Strips the hostname and bucket name, returning only the file path.
 *
 * @param url - GCS or Firebase Storage URL
 * @returns File path portion of the URL
 *
 * @example
 * // GCS URL
 * extractFilePath('https://storage.googleapis.com/my-bucket/path/file.jpg')
 * // Returns: 'path/file.jpg'
 *
 * @example
 * // Firebase Storage URL (encoded slashes)
 * extractFilePath('https://firebasestorage.googleapis.com/v0/b/my-bucket/o/memberships%2Fpath%2Ffile.jpg?alt=media')
 * // Returns: 'memberships/path/file.jpg'
 */
export function extractFilePath(url: string): string {
  try {
    const urlObj = new URL(url)

    // Firebase Storage URLs: firebasestorage.googleapis.com/v0/b/<bucket>/o/<encoded-path>
    // The file path is URL-encoded after /o/ (uses %2F for slashes)
    if (urlObj.hostname === 'firebasestorage.googleapis.com') {
      const parts = urlObj.pathname.split('/').filter(Boolean)
      // Format: ['v0', 'b', '<bucket>', 'o', '<encoded-path>']
      const oIndex = parts.indexOf('o')
      if (oIndex !== -1 && parts.length > oIndex + 1) {
        // The path after /o/ may be a single URL-encoded segment (slashes encoded as %2F)
        // or multiple segments. Join them and decode.
        return decodeURIComponent(parts.slice(oIndex + 1).join('/'))
      }
    }

    // Remove leading slash from pathname
    const path = urlObj.pathname.substring(1)

    // For Google Storage URLs, the format is: bucket-name/path/to/file.jpg
    // We need to remove the bucket name from the path
    const pathParts = path.split('/')
    if (pathParts.length > 1) {
      // Remove first part (bucket name) and rejoin
      return pathParts.slice(1).join('/')
    }

    return path
  } catch {
    // If it's not a valid URL, assume it's already a file path
    return url
  }
}

/**
 * Extracts bucket name from a GCS or Firebase Storage URL
 * Handles: storage.googleapis.com, storage.cloud.google.com, firebasestorage.googleapis.com
 *
 * @param url - Google Cloud Storage or Firebase Storage URL
 * @returns Bucket name or undefined
 *
 * @example
 * extractBucketFromUrl('https://storage.googleapis.com/my-bucket/path/file.jpg')
 * // Returns: 'my-bucket'
 *
 * @example
 * extractBucketFromUrl('https://firebasestorage.googleapis.com/v0/b/my-bucket/o/path%2Ffile.jpg')
 * // Returns: 'my-bucket'
 */
export function extractBucketFromUrl(url: string | undefined): string | undefined {
  if (!url) return undefined
  try {
    const u = new URL(url)
    // firebasestorage.googleapis.com/v0/b/<bucket>/o/<path>
    // MUST be checked before the generic storage.googleapis.com check
    // because 'firebasestorage.googleapis.com'.includes('storage.googleapis.com') is true
    if (u.hostname === 'firebasestorage.googleapis.com') {
      const parts = u.pathname.split('/').filter(Boolean)
      // Format: ['v0', 'b', '<bucket>', 'o', '<encoded-path>']
      const bIndex = parts.indexOf('b')
      if (bIndex !== -1 && parts.length > bIndex + 1) {
        return parts[bIndex + 1]
      }
    }
    // storage.googleapis.com/<bucket>/<path>
    if (u.hostname.includes('storage.googleapis.com')) {
      const parts = u.pathname.split('/').filter(Boolean)
      if (parts.length >= 1) return parts[0]
    }
    // storage.cloud.google.com/<bucket>/<path>
    if (u.hostname.includes('storage.cloud.google.com')) {
      const parts = u.pathname.split('/').filter(Boolean)
      if (parts.length >= 1) return parts[0]
    }
  } catch {
    // ignore parse errors
  }
  return undefined
}

/**
 * Auto-detect app source from bucket name.
 * Returns the specific source identifier. Callers may narrow the type as needed
 * (e.g., video code maps 'client-portal'/'gokollab' to 'communities' for SourceInfo).
 *
 * @param bucket - GCS bucket name
 * @returns App source identifier or null if can't detect
 *
 * @example
 * getSourceFromBucket('revex-membership-production')
 * // Returns: 'courses'
 *
 * @example
 * getSourceFromBucket('highlevel-communities-staging')
 * // Returns: 'communities'
 *
 * @example
 * getSourceFromBucket('crm-events-production')
 * // Returns: 'events-management'
 */
export function getSourceFromBucket(bucket: string): 'communities' | 'courses' | 'client-portal' | 'gokollab' | 'events-management' | null {
  const bucketLower = bucket.toLowerCase()

  // Map bucket names to app sources
  if (bucketLower.includes('communities')) {
    return 'communities'
  }
  if (bucketLower.includes('client-portal') || bucketLower.includes('clientportal')) {
    return 'client-portal'
  }
  if (bucketLower.includes('gokollab')) {
    return 'gokollab'
  }
  if (bucketLower.includes('membership') || bucketLower.includes('course')) {
    return 'courses'
  }
  if (bucketLower.includes('crm-events')) {
    return 'events-management'
  }
  // Return null if can't detect (allows fallback to passed source)
  return null
}

/**
 * Checks if a URL is a valid Google Cloud Storage URL
 *
 * @param url - URL to check
 * @returns True if URL is a GCS URL
 *
 * @example
 * isGCSUrl('https://storage.googleapis.com/my-bucket/file.jpg')
 * // Returns: true
 *
 * @example
 * isGCSUrl('https://cdn.courses.apisystem.tech/file.jpg')
 * // Returns: false
 */
export function isGCSUrl(url: string | undefined): boolean {
  if (!url) return false
  try {
    const urlObj = new URL(url)
    return urlObj.hostname.includes('storage.googleapis.com') || urlObj.hostname.includes('storage.cloud.google.com')
  } catch {
    return false
  }
}

/**
 * Checks if a URL is from a known CDN or GCS (supports both for video/media loading)
 *
 * @param url - URL to check
 * @returns True if URL is a CDN or GCS URL
 *
 * @example
 * isCDNOrGCSUrl('https://storage.googleapis.com/my-bucket/video.mp4')
 * // Returns: true
 *
 * @example
 * isCDNOrGCSUrl('https://cdn.courses.apisystem.tech/video.mp4')
 * // Returns: true
 *
 * @example
 * isCDNOrGCSUrl('https://youtube.com/watch?v=abc')
 * // Returns: false
 */
export function isCDNOrGCSUrl(url: string | undefined): boolean {
  if (!url) return false

  // Check CDN mapping first
  const cdnMapping = getCDNMapping(url)
  if (cdnMapping) return true

  // Check if it's a GCS URL
  return isGCSUrl(url)
}

/**
 * Checks if a value is a relative file path (not a full URL).
 * Relative paths are typically stored in the database without a hostname,
 * e.g., `/memberships/abc/product/xyz` or `memberships/abc/product/xyz`.
 *
 * @param value - URL or file path to check
 * @returns True if the value is a relative path (does not start with http:// or https://)
 *
 * @example
 * isRelativePath('/memberships/knES3eSWYIsc5YSZ3YLl/product/4f5f8dd4-33f4-406c-bfc9-9eaa063cf23c')
 * // Returns: true
 *
 * @example
 * isRelativePath('memberships/path/file.jpg')
 * // Returns: true
 *
 * @example
 * isRelativePath('https://storage.googleapis.com/bucket/file.jpg')
 * // Returns: false
 */
export function isRelativePath(value: string | undefined): boolean {
  if (!value) return false
  return !value.startsWith('http://') && !value.startsWith('https://')
}

/**
 * Checks if a URL points to a GIF image (by file extension)
 *
 * @param url - URL or file path to check
 * @returns True if the URL ends with .gif or contains .gif? (with query params)
 *
 * @example
 * isGif('https://example.com/animation.gif')
 * // Returns: true
 *
 * @example
 * isGif('https://example.com/animation.gif?token=abc')
 * // Returns: true
 *
 * @example
 * isGif('https://example.com/photo.jpg')
 * // Returns: false
 */
export function isGif(url: string): boolean {
  if (!url) return false
  return url.toLowerCase().endsWith('.gif') || url.toLowerCase().includes('.gif?')
}

/**
 * Replaces Google Cloud Storage bucket URLs with CDN URLs.
 * Uses the most complete version with ASSETS_DRM_IMAGE_URL guard to avoid double-transforming DRM-optimized URLs.
 *
 * @param value - URL string or object with backgroundImage property
 * @param options - Optional settings (e.g., skipFirebaseUrlCheck)
 * @returns Transformed URL or original value
 *
 * @example
 * // GCS bucket URL → CDN URL
 * replaceBucketUrlWithCdnUrl('https://storage.googleapis.com/revex-membership-production/memberships/img.jpg')
 * // Returns: 'https://cdn.courses.apisystem.tech/memberships/img.jpg'
 *
 * @example
 * // Already a CDN URL → returned as-is
 * replaceBucketUrlWithCdnUrl('https://cdn.courses.apisystem.tech/memberships/img.jpg')
 * // Returns: 'https://cdn.courses.apisystem.tech/memberships/img.jpg'
 *
 * @example
 * // Relative path → prepended with membership CDN URL
 * replaceBucketUrlWithCdnUrl('/memberships/path/img.jpg')
 * // Returns: 'https://cdn.courses.apisystem.tech/memberships/path/img.jpg'
 */
interface CdnReplacementObject {
  backgroundImage?: string
  [key: string]: unknown
}

function isCdnReplacementObject(value: unknown): value is CdnReplacementObject {
  return typeof value === 'object' && value !== null
}

function containsAnyMarker(value: unknown, markers: readonly string[]): boolean {
  if (typeof value !== 'string') return false
  return markers.some(marker => Boolean(marker && value.includes(marker)))
}

function shouldSkipCdnReplacement(value: unknown, options?: { skipFirebaseUrlCheck?: boolean }): boolean {
  if (!value) return true
  const rawUrl = typeof value === 'string' ? value : isCdnReplacementObject(value) ? value.backgroundImage : undefined
  if (typeof rawUrl !== 'string' || !rawUrl) return true
  if (isAssetsDrmImageUrl(rawUrl)) return true
  const skipMarkers = [
    config.FIREBASE.assetsUrl,
    config.ASSETS_DRM_BUCKET,
    config.ASSETS_DRM_IMAGE_URL,
    ...(options?.skipFirebaseUrlCheck ? [] : [FIREBASE_STORAGE_BASE_URL]),
  ]
  return containsAnyMarker(value, skipMarkers)
}

function rewriteKnownBucketUrl(
  assetUrl: string,
  membershipBucketIndex: number,
  parentBucketIndex: number,
  membershipCdnUrl: string
): string {
  const isMembership = membershipBucketIndex > -1
  const bucket = isMembership ? config.FIREBASE.membershipStorageBucket : config.FIREBASE.storageBucket
  const bucketCdnUrl = isMembership ? membershipCdnUrl : config.FIREBASE.cdnUrl
  const bucketIndex = Math.max(membershipBucketIndex, parentBucketIndex)
  let startIndex = bucketIndex + bucket.length
  if (assetUrl.substring(startIndex, startIndex + 2) === '/o') {
    startIndex += 2
  }
  return `${bucketCdnUrl}${assetUrl.substring(startIndex, assetUrl.length)}`
}

function computeReplacedAssetUrl(
  assetUrl: string,
  gcsObjectPath: string,
  membershipBucketIndex: number,
  parentBucketIndex: number,
  cdnIndex: number,
  membershipCdnHit: number,
  membershipCdnUrl: string
): string | undefined {
  if (gcsObjectPath) {
    return `${membershipCdnUrl}${gcsObjectPath}`
  }
  if (membershipBucketIndex !== -1 || parentBucketIndex !== -1) {
    return rewriteKnownBucketUrl(assetUrl, membershipBucketIndex, parentBucketIndex, membershipCdnUrl)
  }
  if (cdnIndex === -1 && membershipCdnHit === -1 && !/^(https?:)?\/\//i.test(assetUrl)) {
    return membershipCdnUrl + assetUrl
  }
  return undefined
}

function formatReplacedAssetValue<T>(
  value: T,
  assetUrl: string,
  hasBucketRewrite: boolean,
  cdnIndex: number,
  membershipCdnHit: number,
  gcsObjectPath: string,
  newAssetUrl?: string
): T | string | CdnReplacementObject {
  if (isCdnReplacementObject(value)) {
    if (hasBucketRewrite) {
      return { backgroundImage: `url(${newAssetUrl})` }
    }
    if (membershipCdnHit !== -1 && assetUrl) {
      return { ...value, backgroundImage: resolveMembershipCdnUrl(assetUrl) }
    }
    return value
  }
  if (cdnIndex !== -1) return value
  if (membershipCdnHit !== -1 && !gcsObjectPath) {
    return typeof value === 'string' ? resolveMembershipCdnUrl(value) : value
  }
  return newAssetUrl ?? value
}

export const replaceBucketUrlWithCdnUrl = <T = string>(
  value: T,
  options?: { skipFirebaseUrlCheck?: boolean }
): T | string | CdnReplacementObject => {
  try {
    if (shouldSkipCdnReplacement(value, options)) {
      return value
    }

    const assetUrl = isCdnReplacementObject(value) ? value.backgroundImage : typeof value === 'string' ? value : undefined
    if (typeof assetUrl !== 'string') {
      return value
    }
    const membershipBucketIndex = assetUrl.indexOf(config.FIREBASE.membershipStorageBucket)
    const parentBucketIndex = assetUrl.indexOf(config.FIREBASE.storageBucket)
    const cdnIndex = Math.max(assetUrl.indexOf(config.FIREBASE.cdnUrl), assetUrl.indexOf(config.FIREBASE.oldCdnUrl))
    const membershipCdnHit = membershipCdnIndex(assetUrl, config.FIREBASE.membershipCdnUrl)
    const membershipCdnUrl = resolveMembershipCdnUrl(config.FIREBASE.membershipCdnUrl, undefined, assetUrl)
    const gcsObjectPath = membershipGcsObjectPath(assetUrl)
    const hasBucketRewrite = Boolean(gcsObjectPath || membershipBucketIndex !== -1 || parentBucketIndex !== -1)

    const newAssetUrl = computeReplacedAssetUrl(
      assetUrl,
      gcsObjectPath,
      membershipBucketIndex,
      parentBucketIndex,
      cdnIndex,
      membershipCdnHit,
      membershipCdnUrl
    )

    return formatReplacedAssetValue(value, assetUrl, hasBucketRewrite, cdnIndex, membershipCdnHit, gcsObjectPath, newAssetUrl)
  } catch (e) {
    console.error('Error while replacing bucket url with cdn url --> ', e)
    return value
  }
}

/**
 * Resolves the GCS bucket name from a URL using config-based bucket matching.
 * Falls back to the membership storage bucket if no specific bucket is detected.
 *
 * @param url - URL to extract bucket name from
 * @param checkingForRawAsset - If true, also checks against the DRM assets bucket
 * @returns Matched bucket name, or empty string if url is null
 *
 * @example
 * getBucketName('https://storage.googleapis.com/highlevel-backend.appspot.com/path/file.jpg')
 * // Returns: config.FIREBASE.storageBucket (e.g., 'highlevel-backend.appspot.com')
 */
export function getBucketName(url: string | null, checkingForRawAsset = false) {
  if (url && url.includes(config.FIREBASE.storageBucket)) return config.FIREBASE.storageBucket
  else if (url && url.includes(config.FIREBASE.assetsDRMBucket) && checkingForRawAsset) {
    return config.FIREBASE.assetsDRMBucket
  } else if (url) return config.FIREBASE.membershipStorageBucket
  else return ''
}

/**
 * Parses a Membership GCS URL and extracts the bucket, folder path, and file path.
 * Supports both encoded and non-encoded URLs.
 *
 * Example usage:
 *   const url = 'https://storage.googleapis.com/membership_bucket/membership_videos/video.mp4';
 *   const result = parseMembershipGCSUrl(url);
 *   // result = {
 *   //   bucket: 'membership_bucket',
 *   //   folderPath: 'membership_videos',
 *   //   filePath: 'membership_videos/video.mp4'
 *   // }
 *
 * @param {string} gcsUrl - The GCS video URL (encoded or not)
 * @returns {Object} - An object containing bucket, folderPath, and filePath
 * @throws {BadRequestException} - If the URL is invalid or does not contain the required structure
 */
export const parseMembershipGCSUrl = (
  gcsUrl: string
): {
  bucket: string
  folderPath: string
  filePath: string
} => {
  // Decode the URL in case it contains encoded characters
  let url = decodeURIComponent(gcsUrl)

  // Remove any query string and fragment identifiers from the URL
  url = url.split('?')[0].split('#')[0]

  // Remove any trailing slash from the URL
  url = url.replace(/\/+$/, '')

  const memberships = 'memberships'
  const membershipKeyword = `/${memberships}/`

  // Ensure the URL contains the membership keyword
  if (!url.includes(membershipKeyword)) {
    throw new Error(`Invalid URL (${url}): Only Membership bucket URLs are supported.`)
  }

  if (url.includes(config.FIREBASE.oldCdnUrl)) {
    throw new Error('Parsing of old cdn url is not supported')
  }

  let bucket = null
  if (membershipCdnIndex(url, config.FIREBASE.membershipCdnUrl) !== -1) {
    bucket = config.FIREBASE.membershipStorageBucket
  } else if (url.includes(config.FIREBASE.cdnUrl)) {
    bucket = config.FIREBASE.storageBucket
  } else {
    bucket = getBucketName(url)
  }

  if (!bucket) {
    throw new Error('Unable to find bucket name')
  }

  // Split the URL based on the membership keyword
  const [_, filePathPart] = url.split(membershipKeyword)

  // Ensure a valid file path exists after the membership keyword
  if (!filePathPart) {
    throw new Error(`Invalid URL (${url}): No file path found after "${membershipKeyword}".`)
  }

  const filePath = `${memberships}/${filePathPart}`
  const lastSlashIndex = filePath.lastIndexOf('/')

  // Ensure there's a valid folder path
  if (lastSlashIndex === -1) {
    throw new Error(`Invalid URL (${url}): Unable to extract folder path.`)
  }

  const folderPath = filePath.slice(0, lastSlashIndex)

  return {
    bucket,
    folderPath,
    filePath,
  }
}
